// Documents Service - Handle student document uploads
import { apiGet, apiDelete, apiPostMultipart, API_BASE_URL } from '@/utils/api';

// Document types matching backend enum - includes both student and staff document types
export type StudentDocumentType =
  // Identity documents (common)
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'PHOTO'
  // Student-specific documents
  | 'DIPLOME_BAC_SCAN'
  | 'RELEVES_NOTES_BAC'
  | 'DIPLOME_ACCES_SCAN'
  | 'RELEVES_NOTES_ACCES'
  | 'RELEVES_NOTES'
  | 'CV'
  | 'LETTRE_MOTIVATION'
  // Staff-specific documents
  | 'CONTRAT_TRAVAIL'
  | 'DIPLOME_SCAN'
  | 'ATTESTATION_RIB'
  // Professor-specific documents (E-DEP)
  | 'AUTORISATION_ENSEIGNER'
  | 'ENGAGEMENT_PROFESSEUR';

export interface DocumentRecord {
  id: string;
  documentType: StudentDocumentType;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  isVerified: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Upload a document for the current user's profile
 */
export async function uploadDocument(
  documentType: StudentDocumentType,
  file: File
): Promise<DocumentRecord> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  // Log for debugging
  console.log('Uploading document:', { documentType, fileName: file.name, fileSize: file.size, fileType: file.type });

  try {
    return await apiPostMultipart<DocumentRecord>('/profile/documents/upload', formData);
  } catch (error) {
    // Log detailed error for debugging
    console.error('Upload failed:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      // This usually means network error or CORS issue
      console.error('Network error - possible causes: server down, CORS issue, or network disconnection');
    }
    throw error;
  }
}

/**
 * Get all documents for the current user
 */
export async function fetchDocuments(): Promise<DocumentRecord[]> {
  return apiGet<DocumentRecord[]>('/profile/documents');
}

/**
 * Get a specific document by ID
 */
export async function fetchDocument(documentId: string): Promise<DocumentRecord> {
  return apiGet<DocumentRecord>(`/profile/documents/${documentId}`);
}

/**
 * Get document download URL
 */
export function getDocumentDownloadUrl(documentId: string): string {
  return `${API_BASE_URL}/api/profile/documents/${documentId}/download`;
}

/**
 * Get document preview URL (for images/PDFs stored on server)
 */
export function getDocumentPreviewUrl(fileUrl: string): string {
  // If it's a relative URL, prepend the API base (without /api)
  if (fileUrl.startsWith('/uploads')) {
    return `${API_BASE_URL}${fileUrl}`;
  }
  return fileUrl;
}

/**
 * Delete a document
 */
export async function deleteDocument(documentId: string): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/profile/documents/${documentId}`);
}

/**
 * Check if a specific document type has been uploaded
 */
export async function hasDocument(documentType: StudentDocumentType): Promise<boolean> {
  try {
    const documents = await fetchDocuments();
    return documents.some(doc => doc.documentType === documentType);
  } catch {
    return false;
  }
}

/**
 * Get document by type from list
 */
export function getDocumentByType(
  documents: DocumentRecord[],
  documentType: StudentDocumentType
): DocumentRecord | undefined {
  return documents.find(doc => doc.documentType === documentType);
}
